import { useEffect, useState } from "react";
import { BoardStore, BoardStoreSnapshot } from "./board-store";

// Subscribes the component to the local-first board store and (re)hydrates it for `boardId`.
// React 17 has no useSyncExternalStore, so this is the classic external-store subscription:
// snapshot in state, updated on every store notification. The store's getSnapshot returns a
// stable reference between mutations, so this only re-renders when the working set changes.
export function useBoardStore(boardId: string): BoardStoreSnapshot {
  const [snapshot, setSnapshot] = useState<BoardStoreSnapshot>(() => BoardStore.getSnapshot());

  useEffect(() => {
    const update = (): void => setSnapshot(BoardStore.getSnapshot());
    const unsubscribe: () => void = BoardStore.subscribe(update);
    // Catch any change that landed between the initial render and this effect.
    update();
    return unsubscribe;
  }, []);

  useEffect(() => {
    BoardStore.open(boardId);
  }, [boardId]);

  return snapshot;
}

// Subscribe to the store WITHOUT triggering hydration. The board page owns the single
// useBoardStore(boardId) that opens/syncs; the columns use this to read the live snapshot, so N
// columns reading the store never fan out into N syncs.
export function useBoardStoreSnapshot(): BoardStoreSnapshot {
  const [snapshot, setSnapshot] = useState<BoardStoreSnapshot>(() => BoardStore.getSnapshot());

  useEffect(() => {
    const update = (): void => setSnapshot(BoardStore.getSnapshot());
    const unsubscribe: () => void = BoardStore.subscribe(update);
    update();
    return unsubscribe;
  }, []);

  return snapshot;
}
